import type { AdminSelf } from '@saay/api-client';
import { formatTimestamp, formatToman, statusLabel } from '@saay/formatting';
import { Button } from '@saay/shadcn/components/button';
import { ErrorState, StatusChip } from '../kit.js';
import type { ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';

import type {
  AdminReturnRequestSummary,
  BranchRequest,
  PayoutAccountChangeRequest,
} from '../api.js';
import {
  type AdminListMobileSummary,
  AdminListTable,
  type DataTableColumn,
} from '../components/admin-list-table.js';
import { FilterPill, FilterPillBar } from '../components/filter-pills.js';
import { QueryState } from '../components/guard.js';
import { ListFooter, ListLoadMore } from '../components/list-footer.js';
import { ListEmpty, ListSkeleton } from '../components/list-state.js';
import { SurfaceSection } from '../components/surface-section.js';
import { canSeeArea, canUse } from '../permissions.js';
import {
  listStatus,
  OPEN_RETURN_STATUSES,
  useAdminReturnRequests,
  useInboxBranchRequests,
  useInboxPayoutChangeRequests,
} from '../queries.js';
import { proposedText } from './merchant-people.js';

/**
 * «درخواست‌های فروشگاه» on the «درخواست‌ها» page (owner requirement): every request a merchant
 * submits that needs platform action, from every merchant, in one place. Today these are branch
 * requests (`listAdminBranchRequests`, AC §11.2) and payout account change requests
 * (`listPayoutAccountChangeRequests`, AC §11.4). Nothing is decided here: each row opens the place
 * where the admin already acts on it (the merchant's «شعب و کاربران» or «حساب تسویه» tab, or the
 * approval request a change is waiting on). Open return requests are listed read-only as
 * «مرجوعی‌های باز» (RT7, AC §24.10.7) and never add to the inbox count.
 */

export type MerchantRequestStatus = 'open' | 'decided' | 'all';
export type MerchantRequestType = 'branch' | 'payout';

/** The status filter of each list, as the CSV the list endpoints take (AC §4.8). */
const BRANCH_STATUS: Readonly<Record<MerchantRequestStatus, string | undefined>> = {
  all: undefined,
  decided: 'APPROVED,REJECTED',
  open: 'PENDING',
};
const PAYOUT_STATUS: Readonly<Record<MerchantRequestStatus, string | undefined>> = {
  all: undefined,
  decided: 'APPLIED,REJECTED',
  open: 'PENDING,APPROVAL_PENDING',
};

const STATUS_OPTIONS: readonly { key: MerchantRequestStatus; label: string }[] = [
  { key: 'open', label: 'در انتظار بررسی' },
  { key: 'decided', label: 'بررسی‌شده' },
  { key: 'all', label: 'همه' },
];

const TYPE_LABEL: Readonly<Record<MerchantRequestType, string>> = {
  branch: 'درخواست شعبه',
  payout: 'تغییر حساب تسویه',
};

export function readMerchantRequestFilters(params: URLSearchParams): {
  readonly status: MerchantRequestStatus;
  readonly type: MerchantRequestType | undefined;
} {
  const status = params.get('requestStatus');
  const type = params.get('requestType');
  return {
    status: status === 'decided' || status === 'all' ? status : 'open',
    type: type === 'branch' || type === 'payout' ? type : undefined,
  };
}

/** The lists this caller may read (D-31: payout change requests are hidden from Marketing). */
function readable(self: AdminSelf): Readonly<Record<MerchantRequestType, boolean>> {
  return {
    branch: canUse(self, 'listAdminBranchRequests'),
    payout: canUse(self, 'listPayoutAccountChangeRequests'),
  };
}

export function canSeeMerchantRequests(self: AdminSelf): boolean {
  const lists = readable(self);
  return lists.branch || lists.payout;
}

/**
 * The open merchant requests this caller can act on, for the nav and tab badges. It reads the same
 * first page as the default («در انتظار بررسی») view, so the inbox and the badge share one cache
 * entry. A payout change already waiting on a second admin (`APPROVAL_PENDING`) is counted by the
 * approval inbox instead. `more` is true when a list has a further page.
 */
export function useMerchantRequestCount(self: AdminSelf | undefined): {
  readonly count: number;
  readonly more: boolean;
} {
  const branchActor =
    self !== undefined &&
    canUse(self, 'listAdminBranchRequests') &&
    canUse(self, 'approveBranchRequest');
  const payoutActor =
    self !== undefined &&
    canUse(self, 'listPayoutAccountChangeRequests') &&
    canUse(self, 'applyPayoutAccountChangeRequest');
  const branches = useInboxBranchRequests(BRANCH_STATUS.open, branchActor);
  const payouts = useInboxPayoutChangeRequests(PAYOUT_STATUS.open, payoutActor);
  const first = <T extends { readonly status: string }>(
    data: { readonly pages: readonly { readonly items: readonly T[] }[] } | undefined,
  ) => (data?.pages[0]?.items ?? []).filter((item) => item.status === 'PENDING').length;
  return {
    count: (branchActor ? first(branches.data) : 0) + (payoutActor ? first(payouts.data) : 0),
    more: (branchActor && branches.hasNextPage) || (payoutActor && payouts.hasNextPage),
  };
}

function branchTarget(row: BranchRequest): string {
  return `/merchants/${encodeURIComponent(row.merchant.id)}?tab=people`;
}

function payoutTarget(row: PayoutAccountChangeRequest): string {
  if (
    row.status === 'APPROVAL_PENDING' &&
    row.approvalRequestId !== undefined &&
    row.approvalRequestId !== null
  ) {
    return `/approvals/${encodeURIComponent(row.approvalRequestId)}`;
  }
  return `/merchants/${encodeURIComponent(row.owner.id)}?tab=payout`;
}

function actionLabel(status: string): string {
  if (status === 'PENDING') return 'بررسی';
  if (status === 'APPROVAL_PENDING') return 'درخواست تأیید';
  return 'مشاهده';
}

function RequestCell({
  merchant,
  title,
  to,
}: {
  readonly merchant: string;
  readonly title: string;
  readonly to: string;
}) {
  return (
    <span className="saay-cell-main">
      <Link to={to}>{title}</Link>
      <small>{merchant}</small>
    </span>
  );
}

/** The part of a cursor list (`useCursorList`) a section reads. */
interface InboxList<Row> {
  readonly data: { readonly pages: readonly { readonly items: readonly Row[] }[] } | undefined;
  readonly error: unknown;
  readonly fetchNextPage: () => Promise<unknown>;
  readonly hasNextPage: boolean;
  readonly isFetchNextPageError: boolean;
  readonly isFetchingNextPage: boolean;
  readonly refetch: () => Promise<unknown>;
  readonly status: 'pending' | 'error' | 'success';
}

function ListSection<Row extends { readonly id: string; readonly status: string }>({
  columns,
  empty,
  id,
  list,
  mobileSummary,
  target,
  title,
}: {
  readonly columns: readonly DataTableColumn<Row>[];
  readonly empty: string;
  readonly id: string;
  readonly list: InboxList<Row>;
  readonly mobileSummary: AdminListMobileSummary<Row>;
  readonly target: (row: Row) => string;
  readonly title: string;
}) {
  const navigate = useNavigate();
  const rows = list.data?.pages.flatMap((page) => page.items) ?? [];
  return (
    <SurfaceSection id={id} title={title}>
      <QueryState
        empty={<ListEmpty title={empty} />}
        error={list.error}
        isEmpty={rows.length === 0}
        onRetry={() => void list.refetch()}
        skeleton={<ListSkeleton label={`در حال بارگذاری ${title}…`} lines={4} />}
        status={listStatus(list)}
      >
        <AdminListTable<Row>
          caption={`فهرست ${title}`}
          columns={columns}
          mobileSummary={mobileSummary}
          onRowClick={(row) => void navigate(target(row))}
          regionLabel={`جدول ${title}`}
          rowKey={(row) => row.id}
          rows={rows}
        />
        {list.isFetchNextPageError ? (
          <ErrorState error={list.error} onRetry={() => void list.fetchNextPage()} />
        ) : null}
        <ListFooter
          pager={
            <ListLoadMore
              hasMore={list.hasNextPage}
              loading={list.isFetchingNextPage}
              onLoadMore={() => void list.fetchNextPage()}
            />
          }
          shown={rows.length}
          total={undefined}
        />
      </QueryState>
    </SurfaceSection>
  );
}

function requester(row: { readonly requestedBy: { readonly displayNameFa: string | null } }) {
  return row.requestedBy.displayNameFa ?? 'بدون نام';
}

function requestedAt(row: { readonly requestedAt: string }): ReactNode {
  return <time dateTime={row.requestedAt}>{formatTimestamp(row.requestedAt)}</time>;
}

function emptyTitle(status: MerchantRequestStatus, kind: string): string {
  return status === 'open' ? `${kind} در انتظار بررسی نیست` : `${kind} با این فیلترها پیدا نشد`;
}

export function MerchantRequestsView({ self }: { readonly self: AdminSelf }) {
  const [params, setParams] = useSearchParams();
  const filters = readMerchantRequestFilters(params);
  const lists = readable(self);
  const showBranch = lists.branch && filters.type !== 'payout';
  const showPayout = lists.payout && filters.type !== 'branch';
  const branches = useInboxBranchRequests(BRANCH_STATUS[filters.status], showBranch);
  const payouts = useInboxPayoutChangeRequests(PAYOUT_STATUS[filters.status], showPayout);
  const showReturns = canSeeArea(self, 'payments');
  const returns = useAdminReturnRequests({ status: OPEN_RETURN_STATUSES }, showReturns);
  const types = (['branch', 'payout'] as const).filter((type) => lists[type]);

  function change(changes: Readonly<Record<string, string>>) {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(changes)) {
      if (value === '') next.delete(key);
      else next.set(key, value);
    }
    setParams(next);
  }

  return (
    <>
      <FilterPillBar
        activeFilterCount={
          (filters.type === undefined ? 0 : 1) + (filters.status === 'open' ? 0 : 1)
        }
        label="فیلتر درخواست‌های فروشگاه"
        onClearAll={() => change({ requestStatus: '', requestType: '' })}
      >
        <FilterPill
          emptyLabel="در انتظار بررسی"
          label="وضعیت درخواست"
          onChange={(requestStatus) =>
            change({ requestStatus: requestStatus === 'open' ? '' : requestStatus })
          }
          options={STATUS_OPTIONS.filter((option) => option.key !== 'open').map((option) => ({
            label: option.label,
            value: option.key,
          }))}
          value={filters.status === 'open' ? '' : filters.status}
        />
        {types.length > 1 ? (
          <FilterPill
            emptyLabel="همه نوع‌ها"
            label="نوع درخواست"
            onChange={(requestType) => change({ requestType })}
            options={types.map((type) => ({ label: TYPE_LABEL[type], value: type }))}
            value={filters.type ?? ''}
          />
        ) : null}
      </FilterPillBar>
      {showBranch || showPayout ? null : (
        <ListEmpty
          action={
            <Button onClick={() => change({ requestType: '' })} type="button" variant="outline">
              پاک‌کردن فیلتر
            </Button>
          }
          title="این نوع درخواست برای نقش شما در دسترس نیست"
        />
      )}
      {showBranch ? (
        <ListSection<BranchRequest>
          columns={[
            {
              header: 'درخواست',
              id: 'request',
              pin: 'start',
              render: (row) => (
                <RequestCell
                  merchant={row.merchant.displayNameFa}
                  title={statusLabel('branchRequestType', row.requestType)}
                  to={branchTarget(row)}
                />
              ),
            },
            {
              header: 'جزئیات',
              id: 'details',
              render: (row) => (
                <span className="saay-cell-main">
                  <span>{row.branch?.nameFa ?? proposedText(row)}</span>
                  {row.branch === null ? null : <small>{proposedText(row)}</small>}
                </span>
              ),
            },
            { header: 'ثبت‌کننده', id: 'requester', render: requester },
            { header: 'زمان ثبت', id: 'requested', render: requestedAt },
            {
              header: 'وضعیت',
              id: 'status',
              render: (row) => <StatusChip enumName="branchRequestStatus" value={row.status} />,
            },
            {
              header: 'اقدام',
              id: 'action',
              render: (row) => <Link to={branchTarget(row)}>{actionLabel(row.status)}</Link>,
            },
          ]}
          empty={emptyTitle(filters.status, 'درخواست شعبه‌ای')}
          id="merchant-branch-requests"
          list={branches}
          mobileSummary={(row) => ({
            primary: (
              <span className="saay-cell-main">
                <strong>{statusLabel('branchRequestType', row.requestType)}</strong>
                <small>{row.merchant.displayNameFa}</small>
              </span>
            ),
            secondary: <StatusChip enumName="branchRequestStatus" value={row.status} />,
          })}
          target={branchTarget}
          title="درخواست‌های شعبه"
        />
      ) : null}
      {showPayout ? (
        <ListSection<PayoutAccountChangeRequest>
          columns={[
            {
              header: 'درخواست',
              id: 'request',
              pin: 'start',
              render: (row) => (
                <RequestCell
                  merchant={row.owner.displayNameFa}
                  title={TYPE_LABEL.payout}
                  to={payoutTarget(row)}
                />
              ),
            },
            {
              header: 'حساب پیشنهادی',
              id: 'details',
              render: (row) => (
                <span className="saay-cell-main">
                  <span>{row.holderName}</span>
                  <small>
                    <bdi dir="ltr">{row.shebaMasked}</bdi>
                  </small>
                </span>
              ),
            },
            { header: 'ثبت‌کننده', id: 'requester', render: requester },
            { header: 'زمان ثبت', id: 'requested', render: requestedAt },
            {
              header: 'وضعیت',
              id: 'status',
              render: (row) => (
                <StatusChip enumName="payoutChangeRequestStatus" value={row.status} />
              ),
            },
            {
              header: 'اقدام',
              id: 'action',
              render: (row) => <Link to={payoutTarget(row)}>{actionLabel(row.status)}</Link>,
            },
          ]}
          empty={emptyTitle(filters.status, 'درخواست تغییر حسابی')}
          id="merchant-payout-change-requests"
          list={payouts}
          mobileSummary={(row) => ({
            primary: (
              <span className="saay-cell-main">
                <strong>{TYPE_LABEL.payout}</strong>
                <small>{row.owner.displayNameFa}</small>
              </span>
            ),
            secondary: <StatusChip enumName="payoutChangeRequestStatus" value={row.status} />,
          })}
          target={payoutTarget}
          title="درخواست‌های تغییر حساب تسویه"
        />
      ) : null}
      {showReturns ? (
        <ListSection<AdminReturnRequestSummary>
          columns={[
            {
              header: 'مرجوعی',
              id: 'request',
              pin: 'start',
              render: (row) => (
                <RequestCell
                  merchant={row.merchant.nameFa}
                  title={statusLabel('returnType', row.returnType)}
                  to={`/return-requests/${row.id}`}
                />
              ),
            },
            {
              header: 'فروش',
              id: 'sale',
              render: (row) => (
                <bdi dir="ltr" translate="no">
                  {row.saleReference}
                </bdi>
              ),
            },
            {
              header: 'خریدار',
              id: 'buyer',
              render: (row) => row.buyer.displayNameFa ?? row.buyer.displayNameMasked,
            },
            { header: 'مبلغ', id: 'amount', render: (row) => formatToman(row.reductionIrr) },
            {
              header: 'وضعیت',
              id: 'status',
              render: (row) => <StatusChip enumName="returnRequestStatus" value={row.status} />,
            },
            {
              header: 'اقدام',
              id: 'action',
              render: (row) => <Link to={`/return-requests/${row.id}`}>مشاهده</Link>,
            },
          ]}
          empty="مرجوعی بازی نیست"
          id="open-return-requests"
          list={returns}
          mobileSummary={(row) => ({
            primary: (
              <span className="saay-cell-main">
                <strong>{statusLabel('returnType', row.returnType)}</strong>
                <small>{row.merchant.nameFa}</small>
              </span>
            ),
            secondary: <StatusChip enumName="returnRequestStatus" value={row.status} />,
          })}
          target={(row) => `/return-requests/${row.id}`}
          title="مرجوعی‌های باز"
        />
      ) : null}
    </>
  );
}
